// Which areas the Origin-Destination setup page lets a person pick together, and how the picked
// areas travel to the aggregation page (including the older two-sided router state).
// Pure: no React, router or Firebase imports.
import { COUNTRIES, areaCountry } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { PRODUCT_LABELS, areaSupport, geoTypeNoun } from '../config/products';
import { DEFAULT_ODM_DIRECTION, isOdmDirection } from './odmDirection';
import type { OdmDirection } from './odmDirection';

/** A saved area, as it is carried from the setup page to the aggregation page. */
export interface SelectedArea {
  id: string;
  name: string;
  /** The area type (admin_level_2, grid_1000m, ...). */
  type: string;
  ids: string[];
  country_code?: string | null;
}

/** What the setup page knows about a saved area. */
export interface OdmCandidate {
  id: string;
  name: string;
  type: string;
  ids?: string[];
  country_code?: string | null;
}

export type Selectable = { ok: true } | { ok: false; reason: string };

export const MIXED_PROJECT_REASON = 'Areas in this project are of mixed types';
export const EMPTY_PROJECT_REASON = `No area in this project can be used for ${PRODUCT_LABELS.odm}`;

/** The area type a selection stands for; null when nothing is selected. */
export const selectionGeoType = (selection: readonly { type: string }[]): string | null => selection[0]?.type ?? null;

const capitalised = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Pick areas of one type (currently: Municipalities)". */
function oneTypeReason(geoType: string, country: Parameters<typeof geoTypeNoun>[1]): string {
  return `Pick areas of one type (currently: ${capitalised(geoTypeNoun(geoType, country).plural)})`;
}

/** "Pick areas from one country (currently: Estonia)". */
function oneCountryReason(country: CountryCode | null): string {
  return `Pick areas from one country (currently: ${country ? COUNTRIES[country].label : 'no country set'})`;
}

/** The country a selection stands for; null when nothing is selected or its country cannot be told. */
export const selectionCountry = (selection: readonly { type: string; country_code?: string | null }[]) =>
  selection.length > 0 ? areaCountry(selection[0]) : null;

/**
 * May this area be added to the selection? Only areas the product registry serves are eligible
 * (standard polygons, not custom ones), and every area in a selection has the same country and the
 * same type. An area already in the selection is always allowed, so it can be taken out again.
 */
export function odmSelectable(area: OdmCandidate, selection: readonly SelectedArea[]): Selectable {
  const support = areaSupport(area, 'odm');
  if (!support.ok) return { ok: false, reason: support.reason };
  if (!area.ids || area.ids.length === 0) return { ok: false, reason: `This area has no area ids, so ${PRODUCT_LABELS.odm} can't use it.` };
  if (selection.some((s) => s.id === area.id)) return { ok: true };
  if (selection.length > 0) {
    const currentCountry = selectionCountry(selection);
    if (currentCountry !== areaCountry(area)) return { ok: false, reason: oneCountryReason(currentCountry) };
  }
  const current = selectionGeoType(selection);
  if (current !== null && current !== area.type) return { ok: false, reason: oneTypeReason(current, areaCountry(area)) };
  return { ok: true };
}

/**
 * May this project be picked as a whole? Its eligible members (the ones `odmSelectable` accepts
 * on their own, ignoring the current selection) are added together, so they must share one area
 * type. A project is picked whole or not at all.
 */
export function odmProjectSelectable<T extends OdmCandidate>(
  members: readonly T[],
  selection: readonly SelectedArea[],
): { ok: true; areas: T[] } | { ok: false; reason: string } {
  const eligible = members.filter((m) => odmSelectable(m, []).ok);
  if (eligible.length === 0) return { ok: false, reason: EMPTY_PROJECT_REASON };
  const types = new Set(eligible.map((m) => m.type));
  if (types.size > 1) return { ok: false, reason: MIXED_PROJECT_REASON };
  const projectType = eligible[0].type;
  const current = selectionGeoType(selection);
  const alreadyIn = eligible.every((m) => selection.some((s) => s.id === m.id));
  if (selection.length > 0 && !alreadyIn) {
    const currentCountry = selectionCountry(selection);
    if (currentCountry !== areaCountry(eligible[0])) return { ok: false, reason: oneCountryReason(currentCountry) };
  }
  if (current !== null && current !== projectType && !alreadyIn) {
    return { ok: false, reason: oneTypeReason(current, areaCountry(eligible[0])) };
  }
  return { ok: true, areas: eligible };
}

export const toSelectedArea = (a: OdmCandidate): SelectedArea => ({
  id: a.id,
  name: a.name,
  type: a.type,
  ids: [...(a.ids ?? [])],
  country_code: a.country_code ?? null,
});

/** The selection with `areas` added (those already in it are skipped). */
export function addAreas(selection: readonly SelectedArea[], areas: readonly OdmCandidate[]): SelectedArea[] {
  const out = [...selection];
  for (const a of areas) if (!out.some((s) => s.id === a.id)) out.push(toSelectedArea(a));
  return out;
}

/** The selection without the areas whose id is in `ids`. */
export const removeAreas = (selection: readonly SelectedArea[], ids: readonly string[]): SelectedArea[] =>
  selection.filter((s) => !ids.includes(s.id));

/** Every area id the selection holds, once each, in the order they were picked. */
export function selectionAreaIds(selection: readonly { ids?: unknown }[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of selection) {
    if (!Array.isArray(s.ids)) continue;
    for (const id of s.ids) {
      const key = String(id);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    }
  }
  return out;
}

// ── Router state ──────────────────────────────────────────────────────────────────────────────

/** What the aggregation page starts from. */
export interface OdmScope {
  selection: SelectedArea[];
  geoType: string | null;
  geometries: unknown[];
  direction: OdmDirection;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

/** A saved area, or the old municipality dropdown's `{ type: 'municipalities', ids }`, as a SelectedArea. */
function legacySelected(sel: unknown): SelectedArea | null {
  if (!isRecord(sel) || !Array.isArray(sel.ids) || sel.ids.length === 0) return null;
  const ids = sel.ids.map(String);
  if (sel.type === 'municipalities') return { id: 'municipalities', name: 'Municipalities', type: 'admin_level_2', ids };
  if (!isNonEmptyString(sel.type)) return null;
  return {
    id: isNonEmptyString(sel.id) ? sel.id : `${sel.type}:${ids.join(',')}`,
    name: isNonEmptyString(sel.name) ? sel.name : sel.type,
    type: sel.type,
    ids,
    country_code: typeof sel.country_code === 'string' ? sel.country_code : null,
  };
}

const sameIds = (a: readonly string[], b: readonly string[]) => {
  const set = new Set(a);
  return set.size === new Set(b).size && b.every((id) => set.has(id));
};

const asSelectedAreas = (v: unknown): SelectedArea[] =>
  Array.isArray(v)
    ? v.filter(
        (s): s is SelectedArea =>
          isRecord(s) && isNonEmptyString(s.id) && isNonEmptyString(s.type) && Array.isArray(s.ids),
      )
    : [];

/**
 * The selection, area type, shapes and direction the aggregation page starts from, read from
 * `location.state`. Current state holds `selection`. State from before the single picker held
 * `originSelection` and `destinationSelection`: the same areas on both sides mean Between;
 * otherwise it is To / from with the origin as the selection. Anything else gives null.
 */
export function odmScopeFromState(state: unknown): OdmScope | null {
  if (!isRecord(state)) return null;

  const selection = asSelectedAreas(state.selection);
  if (selection.length > 0) {
    return {
      selection,
      geoType: isNonEmptyString(state.geoType) ? state.geoType : selectionGeoType(selection),
      geometries: Array.isArray(state.geometries) ? state.geometries : [],
      direction: isOdmDirection(state.direction) ? state.direction : DEFAULT_ODM_DIRECTION,
    };
  }

  const origin = legacySelected(state.originSelection);
  if (!origin) return null;
  const destination = legacySelected(state.destinationSelection);
  const between = !!destination && origin.type === destination.type && sameIds(origin.ids, destination.ids);
  return {
    selection: [origin],
    geoType: origin.type,
    geometries: Array.isArray(state.originGeometries) ? state.originGeometries : [],
    direction: between ? 'between' : 'to-from',
  };
}
